import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { HtmlOverlay3D } from '@asset-viz/objects/decor/HtmlOverlay3D'
import { TankDecorGroup3D } from '@asset-viz/objects/decor/TankDecorGroup3D'
import {
  SurfaceMeshFeature3D,
  SurfaceMeshSelectionType,
} from '../procedural/SurfaceMeshFeature3D'
import { HtmlOverlayPriority, Vec3 } from '@asset-viz/utils/helpers'
import { useAssetVizLocale } from '@asset-viz/providers/locale'
import React from 'react'
import {
  GeckoComponentPolygon,
  GeckoShellCourse,
  GeckoVerticalTankShell,
} from 'types'

/**
 * A component used to represent a shell course on a component's surface
 * @param component         The shell component model
 * @param model             The shell course model
 * @constructor
 */
export const ShellCourseFeature3D: React.FC<{
  component: GeckoVerticalTankShell
  model: GeckoShellCourse
}> = ({ component, model }) => {
  const {
    show3D,
    showFeatures,
    showFeatureLabels,
    showShellCourseDebugColors,
    clipConfig,
    featureLabelColorString,
  } = useAssetVizInternal()
  const { path, nominal, name } = React.useMemo(() => {
    const nominal = model.wallNominalThickness
    const name = model.name
    const path = GeckoComponentPolygon.CreateFromRect(model.geometry)
    return {
      path: path,
      nominal: nominal,
      name: name,
    }
  }, [model])

  const namePlatePosition = React.useMemo<Vec3>(
    () =>
      show3D
        ? [
            0.875,
            (0.5 * (model.geometry.start.y.num + model.geometry.end.y.num)) /
              component.height.num,
            0.5,
          ]
        : [
            1.0,
            (0.5 * (model.geometry.start.y.num + model.geometry.end.y.num)) /
              component.height.num,
            0,
          ],
    [component, model, show3D]
  )

  const showThisFeatureLabel = React.useMemo(
    () =>
      typeof showFeatureLabels === 'boolean'
        ? showFeatureLabels
        : showFeatureLabels({ feature: model, componentModel: component }),
    [model, component, showFeatureLabels]
  )
  const showThisFeature = React.useMemo(
    () =>
      typeof showFeatures === 'boolean'
        ? showFeatures
        : showFeatures({ feature: model, componentModel: component }),
    [model, component, showFeatures]
  )

  const { formatMeasurement } = useAssetVizLocale()

  return !!clipConfig.displayedSection &&
    clipConfig.displayedSection !== model.name ? null : (
    <group>
      <TankDecorGroup3D yPivot={show3D}>
        {!nominal ? null : (
          <HtmlOverlay3D
            key={`${model.geometry.componentID}_course_${model.id}`}
            anchor='left'
            x={namePlatePosition[0]}
            y={namePlatePosition[1]}
            z={namePlatePosition[2]}
            priority={HtmlOverlayPriority.MajorFeatureNamePlate}
            visible={showThisFeatureLabel && showThisFeature}>
            <div
              style={{
                paddingLeft: '0.5rem',
                lineHeight: 0.8,
                color: featureLabelColorString,
              }}>
              <h4>{name}</h4>
              <small>
                Nominal: {formatMeasurement({ measurement: nominal })}
              </small>
            </div>
          </HtmlOverlay3D>
        )}
      </TankDecorGroup3D>
      <SurfaceMeshFeature3D
        path={path}
        featureModel={model}
        showDebugColors={showShellCourseDebugColors}
        selectionType={SurfaceMeshSelectionType.VizToolTipSelectable}
        userDataName={'course'}
      />
    </group>
  )
}
